import React, { useState, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Box,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { createNodeWithPartnerResponse } from '../../../utils/nodeCreationHelper';
import { convertIdToAlphabet } from '../../../utils/idConverter';

const DEFAULT_AUTHOR = '匿名用戶';
const DEFAULT_NODE_TITLE = '想法';

/**
 * QuestionnaireViewNode - 問卷節點的查看與回答對話框
 *
 * 解析 nodeContent.content（JSON 字串），顯示描述和問題，
 * 讓使用者填寫答案後送出，每個有填寫的答案會直接建立為一個想法節點。
 */
export const QuestionnaireViewNode = ({
  open,
  onClose,
  nodeContent,
  ws,
  groupId,
  activityId,
  graphNodes = [],
  graphEdges = [],
  buildGroupContext,
  mode = 'tagged-forum',
  onRefresh,
}) => {
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successNodes, setSuccessNodes] = useState(null);

  // 解析問卷內容
  const questionnaireData = useMemo(() => {
    if (!nodeContent?.content) return null;
    try {
      const parsed = JSON.parse(nodeContent.content);
      if (parsed && Array.isArray(parsed.questions)) return parsed;
    } catch (_) {}
    return null;
  }, [nodeContent]);

  const isGuestMode = sessionStorage.getItem('isGuestMode') === 'true';

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleClose = () => {
    setAnswers({});
    setError(null);
    setSuccessNodes(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!nodeContent || !questionnaireData) return;

    const resolvedGroupId = groupId || sessionStorage.getItem('groupId');
    const resolvedActivityId = activityId || sessionStorage.getItem('activityId');

    const answersPayload = questionnaireData.questions.map((q) => ({
      questionId: q.id,
      questionText: q.text,
      answer: answers[q.id] || '',
    }));

    setLoading(true);
    setError(null);
    setSuccessNodes(null);

    try {
      const author = localStorage.getItem('userName') || DEFAULT_AUTHOR;
      const submissions = answersPayload
        .map((answer) => ({
          questionText: answer.questionText,
          answerText: (answer.answer || '').trim(),
        }))
        .filter((item) => item.answerText);

      const results = await Promise.allSettled(
        submissions.map(async ({ questionText, answerText }) => {
          const createResult = await createNodeWithPartnerResponse({
            ideaData: {
              title: (questionText || '').trim() || DEFAULT_NODE_TITLE,
              content: answerText,
              tags: 'idea',
              author,
              groupId: resolvedGroupId,
            },
            activityId: resolvedActivityId,
            groupId: resolvedGroupId,
            ws,
            nodes: graphNodes,
            edges: graphEdges,
            buildGroupContext,
            mode,
            parentNodeId: nodeContent.id,
          });

          if (!createResult?.success || !createResult?.nodeId) {
            throw new Error('建立節點失敗');
          }

          return {
            id: createResult.nodeId,
            title: (questionText || '').trim() || DEFAULT_NODE_TITLE,
            content: answerText,
          };
        })
      );

      const created = results
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.value);
      const failedCount = results.length - created.length;

      if (created.length === 0) {
        throw new Error('提交失敗，請稍後再試');
      }
      if (failedCount > 0) {
        setError(`部分回答提交失敗（${failedCount} 題）`);
      }

      setSuccessNodes(created);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('問卷提交失敗:', err);
      setError(
        err.response?.data?.message || '提交失敗，請稍後再試'
      );
    } finally {
      setLoading(false);
    }
  };

  if (!nodeContent) return null;

  // 若無法解析內容，降回一般節點顯示
  if (!questionnaireData) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                borderRadius: '4px',
                padding: '2px 8px',
                fontSize: '0.875rem',
                fontWeight: 'bold',
              }}
            >
              #{convertIdToAlphabet(nodeContent.id)}
            </Box>
            <span>{nodeContent.title}</span>
          </Box>
        </DialogTitle>
        <Divider variant="middle" />
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {nodeContent.content}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>關閉</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const hasAnsweredAll = questionnaireData.questions.every(
    (q) => (answers[q.id] || '').trim()
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="body">
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Box
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              borderRadius: '4px',
              padding: '2px 8px',
              fontSize: '0.875rem',
              fontWeight: 'bold',
            }}
          >
            #{convertIdToAlphabet(nodeContent.id)}
          </Box>
          <span>{nodeContent.title}</span>
          <Chip label="📋 問卷" size="small" color="success" variant="outlined" />
        </Box>
      </DialogTitle>
      <Divider variant="middle" />
      <DialogContent>
        {questionnaireData.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {questionnaireData.description}
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {successNodes ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              已成功提交！共建立了 {successNodes.length} 個想法節點。
            </Alert>
            {successNodes.map((n) => (
              <Box
                key={n.id}
                sx={{
                  border: '1px solid #D1FAE5',
                  borderRadius: 1,
                  p: 1.5,
                  mb: 1,
                  backgroundColor: '#F0FDF4',
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  💡 {n.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {n.content}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Box>
            {questionnaireData.questions.map((q, idx) => (
              <Box key={q.id} sx={{ mb: 2.5 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {idx + 1}. {q.text}
                </Typography>
                <TextField
                  value={answers[q.id] || ''}
                  onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                  placeholder="請在此輸入你的回答"
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  disabled={loading || isGuestMode}
                />
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {successNodes ? '關閉' : '取消'}
        </Button>
        {!successNodes && !isGuestMode && (
          <LoadingButton
            onClick={handleSubmit}
            loading={loading}
            loadingPosition="start"
            variant="contained"
            color="success"
            disabled={!hasAnsweredAll || loading}
          >
            送出回答
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default QuestionnaireViewNode;
